import { Button } from "@/components/ui/button";
import { ArrowRight, Smartphone } from "lucide-react";

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 gradient-hero opacity-90" />
      
      {/* Decorative shapes */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      
      <div className="container relative z-10 mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white/90 text-sm font-medium">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              100% Free — Set up in 60 seconds
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
              Your First Impression,
              <br />
              <span className="text-white/90">Perfected.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 font-medium">
              A free digital business card you can share instantly.
            </p>
            
            <p className="text-lg md:text-xl text-white/80 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Networking events end with forgotten handshakes and lost connections. 
              <strong className="text-white"> QCard gives you a stunning digital profile</strong> that makes follow-up effortless — so every connection counts.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-all">
                Try It Free — Takes 60 Seconds
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 bg-transparent">
                See How It Works
              </Button>
            </div>
            
            <div className="flex items-center gap-6 justify-center lg:justify-start pt-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-400/20 flex items-center justify-center">
                  <span className="text-green-400 text-sm">✓</span>
                </div>
                <span className="text-white/80 text-sm">Free forever</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-400/20 flex items-center justify-center">
                  <span className="text-green-400 text-sm">✓</span>
                </div>
                <span className="text-white/80 text-sm">No credit card</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-400/20 flex items-center justify-center">
                  <span className="text-green-400 text-sm">✓</span>
                </div>
                <span className="text-white/80 text-sm">Ready in 60 seconds</span>
              </div>
            </div>
          </div>
          
          {/* Right Content - Phone Mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative floating">
              {/* Phone Frame */}
              <div className="relative w-72 md:w-80 h-[580px] md:h-[640px] bg-qcard-dark rounded-[3rem] p-3 shadow-2xl shadow-black/30">
                {/* Phone Screen */}
                <div className="w-full h-full bg-gradient-to-b from-slate-900 to-slate-800 rounded-[2.5rem] overflow-hidden relative">
                  {/* Status Bar */}
                  <div className="flex justify-between items-center px-6 pt-4 pb-2 text-white/80 text-xs">
                    <span>9:41</span>
                    <div className="w-20 h-6 bg-black rounded-full" />
                    <div className="flex gap-1">
                      <div className="w-4 h-2 bg-white/60 rounded-sm" />
                    </div>
                  </div>
                  
                  {/* QCard Profile */}
                  <div className="px-6 py-8 text-center">
                    {/* Profile Photo */}
                    <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-primary to-qcard-coral p-1 mb-4">
                      <div className="w-full h-full rounded-full bg-slate-700 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">JD</span>
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-1">John Doe</h3>
                    <p className="text-primary font-medium mb-1">CEO & Founder</p>
                    <p className="text-white/60 text-sm mb-6">Acme Corporation</p>
                    
                    {/* Add to Contacts Button */}
                    <button className="w-full gradient-primary text-white font-semibold py-4 rounded-2xl mb-6 flex items-center justify-center gap-2 shadow-lg">
                      <Smartphone className="w-5 h-5" />
                      Add to Contacts
                    </button>
                    
                    {/* Contact Info */}
                    <div className="space-y-3 text-left">
                      <div className="flex items-center gap-3 text-white/80 bg-white/5 rounded-xl p-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-primary">📱</span>
                        </div>
                        <div>
                          <p className="text-xs text-white/50">Mobile</p>
                          <p className="text-sm">+1 (555) 123-4567</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-white/80 bg-white/5 rounded-xl p-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-primary">✉️</span>
                        </div>
                        <div>
                          <p className="text-xs text-white/50">Email</p>
                          <p className="text-sm">john@acme.com</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Glow Effect */}
              <div className="absolute -inset-10 bg-primary/20 rounded-full blur-3xl -z-10" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
